import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  Credential,
  CredentialEntity,
  CredentialEntityToCredentialMapper,
  CredentialToCredentialEntityMapper,
  ServiceProvider,
} from '@uploadcam/common';
import { DialogService, MessageType } from '../../services/dialog.service';
import { getLogger } from '../../../../core/utils';
import { environment } from '../../../../../environments/environment';
import { CREDENTIALS_KEY } from '../../../../../features/auth/domain/interactors/authenticate-with-credentials.interactor';
import { GoogleIdentityService } from '../../../../../features/common/google/google-identity.service';

export interface UpgradeScopesDialogInput {
  credential: Credential;
  scopes: string[];
}

export type UpgradeScopesDialogResult = undefined | Credential;

@Component({
  selector: 'app-upgrade-scopes-dialog',
  templateUrl: './upgrade-scopes-dialog.component.html',
  styleUrls: ['./upgrade-scopes-dialog.component.scss'],
  standalone: false,
})
export class UpgradeScopesDialogComponent {
  private readonly logger = getLogger('UpgradeScopesDialogComponent');
  private readonly credentialEntityToCredential = new CredentialEntityToCredentialMapper();
  private readonly credentialToCredentialEntity = new CredentialToCredentialEntityMapper();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: UpgradeScopesDialogInput,
    private readonly dialog: DialogService,
    private readonly dialogRef: MatDialogRef<
      UpgradeScopesDialogComponent,
      UpgradeScopesDialogResult
    >,
    private readonly googleIdentityService: GoogleIdentityService,
  ) {
    this.dialogRef.disableClose = true;
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public async confirm(): Promise<void> {
    try {
      switch (this.data.credential.provider) {
        case ServiceProvider.Google:
          this.closeDialog(await this.upgradeGoogleScopes());
          return;
      }

      // TODO: Implement upgrade for other providers
      this.closeDialog();
    } catch (err) {
      this.dialog.showErrorAlert(err, 'error.fallback.upgradeScopes');
      this.logger.error('Failed to upgrade scopes:', err);
    }
  }

  private closeDialog(credential?: Credential): void {
    if (credential) {
      this.dialog.snack('upgradeScopesDialog.success', MessageType.Success);
    } else {
      this.dialog.snack('upgradeScopesDialog.error', MessageType.Error);
    }

    this.dialogRef.close(credential);
  }

  private async upgradeGoogleScopes(): Promise<Credential | undefined> {
    this.logger.info('Upgrade Google OAuth Scopes');

    await this.googleIdentityService.load();

    return new Promise<Credential | undefined>((resolve, reject) => {
      const closeWaitFn = this.dialog.wait('upgradeScopesDialog.waitMessage');
      const newScopes = this.data.scopes.join(' ');
      const tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: environment.google.clientId,
        scope: newScopes,
        callback: (tokenResponse) => {
          if (tokenResponse && tokenResponse.access_token) {
            const credential = this.data.credential;
            const scopes = Array.from(
              new Set([...credential.scopes, ...this.data.scopes]).values(),
            );
            const credentialEntity: CredentialEntity = {
              ...this.credentialToCredentialEntity.map(credential),
              accessToken: tokenResponse.access_token,
              scopes,
            };

            localStorage.setItem(CREDENTIALS_KEY, JSON.stringify(credentialEntity));
            this.logger.log('Credential updated with new scopes:', credentialEntity.scopes);

            resolve(this.credentialEntityToCredential.map(credentialEntity));
          } else {
            resolve(undefined);
          }

          closeWaitFn();
        },
        error_callback: (err) => {
          this.logger.error('Failed to upgrade OAuth scopes:', err);
          closeWaitFn();
          reject(err);
        },
      });

      tokenClient.requestAccessToken();
    });
  }
}
